import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { formatAssistanceMinutes } from "../utils/assistanceTime";
import { scheduleDuration } from "../utils/assistanceSchedule";

const months = ["Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"];
const currentDate = new Date();
const monthKey = (year, month) => `${year}-${String(month + 1).padStart(2, "0")}`;
const visitDateKey = (value) => {
  if (typeof value === "string") return value.slice(0, 10);
  const date = value?.toDate instanceof Function ? value.toDate() : value instanceof Date ? value : null;
  return date && Number.isFinite(date.getTime())
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : "";
};
const clientName = (client) => `${client.firstName || ""} ${client.lastName || ""}`.trim() || client.name || "Namnlös klient";

export default function AssistanceClientOverview({ companyUid }) {
  const [clientsData, setClientsData] = useState({ companyUid: "", clients: [], error: "" });
  const [visitsData, setVisitsData] = useState({ companyUid: "", visits: [], error: "" });
  const [tasksData, setTasksData] = useState({ companyUid: "", tasks: [], error: "" });
  const [year, setYear] = useState(String(currentDate.getFullYear()));
  const [month, setMonth] = useState(String(currentDate.getMonth()));
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "clients"), (snapshot) => {
      setClientsData({ companyUid, clients: snapshot.docs.map((item) => ({ ...item.data(), id: item.id })), error: "" });
    }, () => setClientsData({ companyUid, clients: [], error: "Kunde inte hämta klienterna." }));
  }, [companyUid]);

  useEffect(() => {
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "assistanceVisits"), (snapshot) => {
      setVisitsData({ companyUid, visits: snapshot.docs.map((item) => ({ ...item.data(), id: item.id })), error: "" });
    }, () => setVisitsData({ companyUid, visits: [], error: "Kunde inte hämta besöken." }));
  }, [companyUid]);

  useEffect(() => {
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "assistanceTasks"), (snapshot) => {
      setTasksData({ companyUid, tasks: snapshot.docs.map((item) => ({ ...item.data(), id: item.id })), error: "" });
    }, () => setTasksData({ companyUid, tasks: [], error: "Kunde inte hämta klientuppgifterna." }));
  }, [companyUid]);

  const clients = clientsData.companyUid === companyUid ? clientsData.clients : [];
  const visits = visitsData.companyUid === companyUid ? visitsData.visits : [];
  const tasks = tasksData.companyUid === companyUid ? tasksData.tasks : [];
  const activeVisits = useMemo(() => visits.filter((visit) => {
    const sourceTaskId = String(visit.sourceTaskId || "").replace(/^catalog:/, "");
    return tasks.some((task) => task.clientId === visit.clientId && (sourceTaskId ? task.id === sourceTaskId : task.name === visit.taskName));
  }), [visits, tasks]);
  const years = useMemo(() => [...new Set([
    currentDate.getFullYear(),
    ...activeVisits.map((visit) => Number(visitDateKey(visit.date).slice(0, 4)) || null).filter(Boolean),
  ])].sort((a, b) => b - a).map(String), [activeVisits]);
  const rows = useMemo(() => {
    const totals = new Map();
    const clientById = new Map(clients.map((client) => [client.id, client]));
    const clientByName = new Map(clients.map((client) => [clientName(client).toLocaleLowerCase("sv"), client]));
    activeVisits.forEach((visit) => {
      const date = visitDateKey(visit.date);
      if (!date || date.slice(0, 7) !== monthKey(year, Number(month))) return;
      const matchedClient = clientById.get(visit.clientId) || clientByName.get((visit.clientName || "").trim().toLocaleLowerCase("sv"));
      const key = matchedClient?.id || visit.clientId || `name:${(visit.clientName || "Okänd klient").trim().toLocaleLowerCase("sv")}`;
      const total = totals.get(key) || { minutes: 0, visits: 0 };
      total.minutes += scheduleDuration(visit);
      total.visits += 1;
      if (!total.name) total.name = matchedClient ? clientName(matchedClient) : (visit.clientName || "Okänd klient");
      totals.set(key, total);
    });
    const rowsById = new Map(clients.map((client) => {
      const total = totals.get(client.id) || { minutes: 0, visits: 0 };
      return [client.id, { id: client.id, name: clientName(client), minutes: total.minutes, visitCount: total.visits }];
    }));
    totals.forEach((total, id) => {
      if (!rowsById.has(id)) rowsById.set(id, { id, name: total.name || "Okänd klient", minutes: total.minutes, visitCount: total.visits });
    });
    return [...rowsById.values()].filter((row) => row.visitCount > 0 && row.name.toLocaleLowerCase("sv").includes(search.trim().toLocaleLowerCase("sv")))
      .sort((a, b) => a.name.localeCompare(b.name, "sv"));
  }, [clients, activeVisits, year, month, search]);
  const totalMinutes = rows.reduce((sum, row) => sum + row.minutes, 0);
  const totalVisits = rows.reduce((sum, row) => sum + row.visitCount, 0);
  return <section className="client-overview" aria-label="Klientöversikt">
    <div className="client-overview__filters">
      <label className="client-overview__search"><span className="client-overview__visually-hidden">Sök klient</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Sök på klient..." /></label>
      <label><span>År</span><select value={year} onChange={(event) => setYear(event.target.value)}>{years.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
      <label><span>Månad</span><select value={month} onChange={(event) => setMonth(event.target.value)}>{months.map((label, index) => <option key={label} value={index}>{label}</option>)}</select></label>
    </div>
    {!companyUid ? <p>Välj ett företag för att se klientöversikten.</p>
      : clientsData.error || visitsData.error || tasksData.error ? <p role="alert">{clientsData.error || visitsData.error || tasksData.error}</p>
        : clientsData.companyUid !== companyUid || visitsData.companyUid !== companyUid || tasksData.companyUid !== companyUid ? <p role="status">Hämtar klientöversikt…</p>
          : <>
            <div className="client-overview__summary">
              <h1>Klientöversikt ({rows.length})</h1>
              <div><span><strong>Totalt antal besök:</strong> {totalVisits}</span><span><strong>Totala timmar:</strong> {(totalMinutes / 60).toFixed(2)} h</span></div>
            </div>
            <div className="client-overview__table"><table>
            <caption className="smart-assistance__visually-hidden">Besökstid per klient för {months[Number(month)]} {year}</caption>
            <colgroup><col className="client-overview__name-column" /><col className="client-overview__time-column" /><col className="client-overview__count-column" /></colgroup>
            <thead><tr><th>KLIENT</th><th>TOTALT (TIMMAR)</th><th>ANTAL BESÖK</th></tr></thead>
            <tbody>{rows.map((row) => <tr key={row.id}><td><strong>{row.name}</strong></td><td>{(row.minutes / 60).toFixed(2)}</td><td>{row.visitCount}</td></tr>)}
              {rows.length === 0 && <tr><td colSpan="3">Inga klienter hittades för vald månad.</td></tr>}
            </tbody>
          </table></div>
          </>}
  </section>;
}
